import type { Page } from '../../types';
import {
  disposalAssetTenureOptions,
  disposalPortfolioTypeOptions,
  disposalRegionOptions,
} from './data';

const siteDetails: Page[] = [
  {
    pageType: 'question',
    title: 'Site details',
    name: 'site-details',
    description:
      'Complete the basic site identification fields for the disposal record. These fields are mandatory in the source template.',
    questions: [
      {
        questionType: 'textInput',
        title: 'InSite property reference',
        name: 'insite-property-reference',
        hint: 'Enter the InSite property reference relating to the asset.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Departmental ID',
        name: 'departmental-id',
        hint: "Enter the department's unique ID for the asset.",
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Land/ property name',
        name: 'land-property-name',
        hint: 'Use the asset name listed on InSite or the departmental asset database.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textarea',
        title: 'Address',
        name: 'address',
        hint: 'Enter the full site address as listed on InSite or the departmental asset database.',
        textRows: 4,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Postcode',
        name: 'postcode',
        hint: 'Use the Royal Mail postcode for the asset, or the nearest adjacent postcode if none is allocated.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Region',
        name: 'region',
        hintOption: 'Select a region',
        options: disposalRegionOptions,
        detailsHeading: 'Region guidance',
        detailsContent:
          'The workbook references the NUTS region list. See <a href="https://www.ons.gov.uk/methodology/geography/ukgeographies/eurostat" target="_blank" rel="noopener noreferrer">ONS guidance</a> if you need help choosing the correct region.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Asset tenure',
        name: 'asset-tenure',
        hintOption: 'Select an asset tenure',
        options: disposalAssetTenureOptions,
        hint: 'Choose FH, LH, Other Legal Interest or Mixed Tenure.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Site Area (ha)',
        name: 'site-area-ha',
        hint: 'Enter the surplus area of the asset in hectares.',
        placeholder: '0.00',
        rules: {
          required: true,
          float: true,
        },
      },
      {
        questionType: 'select',
        title: 'Portfolio Type',
        name: 'portfolio-type',
        hintOption: 'Select a portfolio type',
        options: disposalPortfolioTypeOptions,
        hint: 'Choose the primary use of the site, aligned to the SOFTE portfolio descriptors.',
        detailsHeading: 'More information',
        detailsContent: `<p>Defines the <strong>primary use of the site</strong> (aligning to SOFTE Portfolio Type descriptors):</p>
<ul>
  <li>Courts and Tribunals = Operational courts and tribunal buildings, administrative buildings and storage sites</li>
  <li>Cultural Assets = Museums (including front of house and back office estate where relevant), galleries, libraries, cinemas, including art storage where relevant</li>
  <li>Defence = Assets from which the MOD lives, works, trains, operates and deploys military capability</li>
  <li>Health - Secondary Care = NHS Trust hospitals (secondary care)</li>
  <li>Health - Primary Care &amp; Community = health clinics and community hospitals (primary care)</li>
  <li>Health - Community, Other Health &amp; Social Care = residential and care facilities (other)</li>
  <li>Infrastructure = Includes but not limited to telemasts, railways, bridges</li>
  <li>Job Centres = Job centres and assessment centres</li>
  <li>Land = Includes but not limited to training land, nature conservations, woodlands, lakes</li>
  <li>Logistics and storage = Includes but not limited to document stores, fuel testing barns, salt barns, vehicle depots, motorway service compounds</li>
  <li>Offices = Offices, including front of house, HQ, service supporting and serviced offices, IT/data centres and call centres</li>
  <li>Overseas = All assets outside the UK</li>
  <li>Prison = Prisons, prison officer's quarters and other associated buildings</li>
  <li>Probation = Probation centres, contact centres and approved premises</li>
  <li>Schools = State funded primary and secondary schools, sixth form colleges, including academies, maintained nurseries, special schools and alternative provision</li>
  <li>Science = Laboratories, specialist science sites and research facilities</li>
  <li>Remaining Estate = All assets which cannot be catergorised into the above portfolios or where a breakdown of your estate cannot be provided</li>
</ul>`,
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default siteDetails;
